// Source profiling helpers for choosing `full` vs `vendor-fast` compile modes.
// Scanner ignores comments/quotes to avoid false-positive token detection.
import type { LuisCompileMode } from '../types'

export type LuisSourceProfile = {
  hasFunctionSyntax: boolean
  hasControlFlowSyntax: boolean
  hasLoopPlaceholderSyntax: boolean
  hasClassVarUsage: boolean
  hasTopLevelLuisOnlyPatterns: boolean
  recommendedMode: LuisCompileMode
}

export type LuisCompileModeResolution = {
  mode: LuisCompileMode
  downgradedFromOverride: boolean
}

// Build source profile flags used by compiler-mode selection.
export function profileLuisSource(source: string): LuisSourceProfile {
  let hasFunctionSyntax = false
  let hasControlFlowSyntax = false
  let hasLoopPlaceholderSyntax = false
  let hasClassVarUsage = false

  let quote: '"' | "'" | null = null
  let inLineComment = false
  let inBlockComment = false

  // Scan source once and detect only meaningful Lenty control tokens.
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index]
    const next = source[index + 1] ?? ''
    const previous = index > 0 ? source[index - 1] : ''

    // Keep line comments opaque during token detection.
    if (inLineComment) {
      if (char === '\n') {
        inLineComment = false
      } else {
        // Continue line-comment scan.
      }
      continue
    } else if (inBlockComment) {
      // Keep block comments opaque during token detection.
      if (char === '*' && next === '/') {
        inBlockComment = false
        index += 1
      } else {
        // Continue block-comment scan.
      }
      continue
    } else {
      // Continue scanner outside comments.
    }

    // Keep quoted strings opaque during token detection.
    if (quote) {
      if (char === quote && previous !== '\\') {
        quote = null
      } else {
        // Continue quoted-string scan.
      }
      continue
    } else {
      // Continue scanner outside quoted strings.
    }

    // Enter comment modes on comment delimiters.
    if (char === '/' && next === '/') {
      inLineComment = true
      index += 1
      continue
    } else if (char === '/' && next === '*') {
      inBlockComment = true
      index += 1
      continue
    } else {
      // Continue token detection outside comments.
    }

    // Enter quote mode on string delimiters.
    if (char === '"' || char === "'") {
      quote = char
      continue
    } else {
      // Continue token detection outside quotes.
    }

    // Detect function declaration/call markers.
    if (char === '@' && startsWithToken(source, index, '@fun:')) {
      hasFunctionSyntax = true
    } else if (char === '@' && startsWithToken(source, index, '@fun.')) {
      hasFunctionSyntax = true
    } else if (char === '@' && startsWithToken(source, index, '@fun ')) {
      hasFunctionSyntax = true
    } else {
      // Continue scanner when function token is absent.
    }

    // Detect control-flow markers used by loop/if preprocess.
    if (char === '@' && startsWithToken(source, index, '@for')) {
      hasControlFlowSyntax = true
    } else if (char === 'f' && startsWithBoundaryToken(source, index, 'for(')) {
      hasControlFlowSyntax = true
    } else if (char === '@' && startsWithToken(source, index, '@if')) {
      hasControlFlowSyntax = true
    } else if (char === 'e' && startsWithBoundaryToken(source, index, 'elseif(')) {
      hasControlFlowSyntax = true
    } else if (char === 'e' && startsWithBoundaryToken(source, index, 'elseif (')) {
      hasControlFlowSyntax = true
    } else if (char === 'e' && startsWithBoundaryToken(source, index, 'else{')) {
      hasControlFlowSyntax = true
    } else if (char === 'e' && startsWithBoundaryToken(source, index, 'else {')) {
      hasControlFlowSyntax = true
    } else {
      // Continue scanner when control-flow token is absent.
    }

    // Detect loop placeholder syntax used by for/if expression replace.
    if (char === '$') {
      const nextChar = source[index + 1] ?? ''
      if (nextChar === '{' || isIdentifierStart(nextChar)) {
        hasLoopPlaceholderSyntax = true
      } else {
        // Continue scanner when `$` is not a placeholder.
      }
    } else {
      // Continue scanner when `$` marker is absent.
    }

    // Detect class-variable composition and object/array var access.
    if (char === '&' && startsWithToken(source, index + 1, 'var(')) {
      hasClassVarUsage = true
    } else if (char === 'v' && startsWithToken(source, index, 'var(--')) {
      const varCallClose = findVarCallClose(source, index + 5)
      if (varCallClose !== -1) {
        const variableBody = source.slice(index + 5, varCallClose).trim()
        if (variableBody.includes('.') || variableBody.includes('[')) {
          hasClassVarUsage = true
        } else {
          // Keep class-var flag unchanged for plain css variable usage.
        }
      } else {
        // Keep class-var flag unchanged for malformed var calls.
      }
    } else {
      // Continue scanner when class-var token is absent.
    }

    // Stop scan early when every expensive-mode signal is already true.
    if (hasFunctionSyntax && hasControlFlowSyntax && hasLoopPlaceholderSyntax && hasClassVarUsage) {
      break
    } else {
      // Continue scanning until all mode signals are determined.
    }
  }

  const hasTopLevelLuisOnlyPatterns = hasFunctionSyntax
    || hasControlFlowSyntax
    || hasLoopPlaceholderSyntax
    || hasClassVarUsage
  const recommendedMode: LuisCompileMode = hasTopLevelLuisOnlyPatterns ? 'full' : 'vendor-fast'
  // Return scanned source profile for compile-mode selection.
  return {
    hasFunctionSyntax,
    hasControlFlowSyntax,
    hasLoopPlaceholderSyntax,
    hasClassVarUsage,
    hasTopLevelLuisOnlyPatterns,
    recommendedMode,
  }
}

// Resolve effective compile mode with safe downgrade from invalid override.
export function resolveCompileMode(
  profile: LuisSourceProfile,
  modeOverride: LuisCompileMode | undefined,
): LuisCompileModeResolution {
  // Use profile recommendation when caller did not provide override.
  if (!modeOverride) {
    return { mode: profile.recommendedMode, downgradedFromOverride: false }
  } else if (modeOverride === 'full') {
    // Keep full mode when override explicitly requests it.
    return { mode: 'full', downgradedFromOverride: false }
  } else if (profile.hasTopLevelLuisOnlyPatterns) {
    // Downgrade unsafe vendor-fast override when source needs full preprocess.
    return { mode: 'full', downgradedFromOverride: true }
  } else {
    // Keep vendor-fast override for source that has no Lenty-only syntax.
    return { mode: 'vendor-fast', downgradedFromOverride: false }
  }
}

// Check whether source starts with token at exact scanner index.
function startsWithToken(source: string, index: number, token: string): boolean {
  // Return true when source matches token at requested offset.
  return source.slice(index, index + token.length) === token
}

// Check boundary-sensitive tokens to avoid mid-word false positives.
function startsWithBoundaryToken(source: string, index: number, token: string): boolean {
  const previous = index > 0 ? source[index - 1] : ''
  // Reject token matches when previous char is identifier-like.
  if (isIdentifierBody(previous)) {
    return false
  } else {
    // Continue boundary-safe token match.
  }

  // Return true when token matches at index and boundary is valid.
  return startsWithToken(source, index, token)
}

// Find matching `)` cursor for one `var(--` call body.
function findVarCallClose(source: string, startIndex: number): number {
  let depth = 1
  // Scan until var() parenthesis depth returns to zero.
  for (let index = startIndex; index < source.length; index += 1) {
    const char = source[index]
    if (char === '(') {
      depth += 1
    } else if (char === ')') {
      depth -= 1
      if (depth === 0) {
        return index
      } else {
        // Continue nested parenthesis scan.
      }
    } else {
      // Continue scan for non-parenthesis chars.
    }
  }

  // Return -1 when var() call is unclosed.
  return -1
}

// Check whether char can start an identifier token.
function isIdentifierStart(char: string): boolean {
  return /[a-zA-Z_]/.test(char)
}

// Check whether char can be inside identifier token.
function isIdentifierBody(char: string): boolean {
  return /[a-zA-Z0-9_]/.test(char)
}
